import * as d3 from 'd3-selection'
import * as array from 'd3-array'
import * as scale from 'd3-scale'
import { adjustLightness } from 'scoreboard-colors'
import horizontal from './horizontal.js'
import vertical from './vertical.js'

function labels (selection, dimensions, scaler) {
  selection.each(function (data) {
    var container = d3.select(this)
                      .selectAll('g.threshold-labels')
                      .data(function (d) {
                        return d.thresholdLabels ? [d] : []
                      })
      , enter = container.enter()
                         .append('g')
                         .attr('class', 'threshold-labels')
      , labels = container.merge(enter)
                          .selectAll('g.threshold-label')
                          .data(function (d) {
                            return (data.thresholdLabels || []).map(function (threshold, i, all) {
                                     var _threshold = i === 0 ? d._thresholds[i] : d._thresholds[i - 1]
                                       , value = i === 0 ? _threshold.y0 : _threshold.y1
                                       , position = (dimensions.spaceBetweenEachBar * i) + scaler(value)

                                     if (i > 0) {
                                       position -= dimensions.spaceBetweenEachBar / 2 // center them
                                     }

                                     if (i == all.length - 1) {
                                       position -= dimensions.spaceBetweenEachBar / 2
                                     }

                                     return {
                                       _threshold: _threshold,
                                       value: value,
                                       position: position,
                                       label: threshold
                                     }
                                   })
                          })
                          .join('g')
                          .attr('class', 'threshold-label')

    container.exit()
             .remove()

    labels.selectAll('text.threshold-label')
          .data(d => [d])
          .join('text')
          .attr('class','threshold-label')
          .attr('dy', 0) // reset each time in case flipping between horizontal and vertical
          .text(d => d.label)
  })
}

function getActiveIndices (data) {
  if (!data.colors) {
    return []
  }

  if (!data.value) {
    return [] // No active indices
  }

  let color = data.value && data.value.color
    , raw = data.value.raw

  // Build an array of the index of the colors that match the current color
  let indices = data.colors.reduce((p, c, i) => {
    if (c == color) {
      p.push(i)
    }
    return p
  }, [])

  if (indices.length === 1) {
    // Easy, no duplicate colors.
    return indices
  }

  // Check if the two colors are next to each other and
  // the raw value is equal to the second color's threshold
  if (indices[1] - indices[0] === 1 && data.thresholds[indices[1]] === raw) {
    // Duplicate colors next to each other and the raw value is
    // locked to the 2nd color's start threshold, so we want to highlight both
    return indices
  }

  // If we made it this far, there are duplicate colors (e.g. 3 color stabilize)
  // We need to determine which color is closest to our actual value
  let rawIdx = array.bisectLeft(data.thresholds, raw)

  // If the raw index is on the first half of thresholds, use the first color
  // otherwise use the second color
  return rawIdx < data.thresholds.length / 2 ? [indices[0]] : [indices[1]]
}

function rect (selection, id) {
  selection.each(function (data) {
    let rect = d3.select(this)
                 .selectAll('rect')
                 .data(function (d) {
                   return d._thresholds
                 })

    rect.exit().remove()

    // Determine the active blocks
    let activeIndices = getActiveIndices(data)

    rect.enter()
        .append('rect')
        .merge(rect)
        .attr('class', 'threshold')
        .style('fill', d => adjustLightness(d.color, 30)) // Matches scoreboard colors `lighter-fill-darker-stroke` mixin. Mix 30% white into the fill
        .style('stroke', d => adjustLightness(d.color, -12)) // mixes 12% black into the stroke
        .classed('active', (d, i) => activeIndices.indexOf(i) !== -1)
  })
}

function currentLabel (selection) {
  selection.each(function (data){
    var group = d3.select(this)
                  .selectAll('g.current-label')
                  .data(function (d) {
                    // We show the current label if there is a `formatted` value.
                    // The formatted value could be empty, and if that's the case we'll inject a `&nbsp;`
                    // a bit later
                    return d.value && typeof d.value.formatted !== 'undefined' ? [d.value] : []
                  })
      , _group = group.enter()
                      .append('g')
                        .attr('class', 'current-label')
                        .merge(group)
                        .attr('transform', 'translate(0,0)') // Always reset to prevent jumping b/c of precision w/ getBoundingClientRect
      , value = _group.selectAll('g.text-value')
                      .data(d => [d.formatted])
                      .join('g')
                      .attr('class', 'text-value')
                      .selectAll('text.value')
                      .data(d => [d])
      , units = _group.selectAll('g.text-units')
                      .data(function (d) {
                        return data.units && // Must have units
                              d // And showing a value
                              ? [data.units] : []
                      })
                      .join('g')
                      .attr('class', 'text-units')
                      .attr('transform', 'translate(0, 0)') //reset in case it flipped between vertical and horizontal
                      .selectAll('text.units')
                      .data(d => [d])

    group.exit().remove()

    var text = value.enter()
                    .append('text')
                    .attr('class', 'value')
                    .merge(value)
                    .attr('dy', 0) // reset each time in case flipping between horizontal and vertical
                    .text(String)

    value.exit().remove()

    units.enter()
         .append('text')
         .attr('class', 'units')
         .merge(units)
         .text(String)

    if (text.node() && !text.text()) {
      // Formatted value space should be there. We have to inject a `&nbsp;` so there's substance when asking for the label height to determine
      // other components position
      text.html('&nbsp;')
    }

    units.exit().remove()
  })
}

function actual (selection, validThresholds) {
  selection.each(function (data) {
    var actual =  d3.select(this)
                    .selectAll('path.value-indicator')
                        .data(function (d) {
                          return d.value && !isNaN(d.value.raw) && validThresholds > 0 ? [d.value] : []
                        })

    actual.enter()
          .append('path')
          .merge(actual)
          .attr('class', 'value-indicator current')
          .style('fill', d => d.color)
          .raise() // Move to the top of the stack so it's not hidden by the threshold rects

    actual.exit()
          .remove()
  })

}

function previous (selection, dimensions, _thresholds, bisector, validThresholds) {
  selection.each(function (data) {
    var previous = d3.select(this)
                     .selectAll('path.previous')
                     .data(function (d) {
                       var thresholdGroup = bisector(data._thresholds, d.previous)
                         , offset = 0

                       if (data._thresholds[thresholdGroup]) {
                         offset = data._thresholds[thresholdGroup].offset
                       } else {
                         offset = data._thresholds[data._thresholds.length - 1].offset + (dimensions.spaceBetweenEachBar / 2)
                       }

                       return typeof d.previous !== 'undefined' && validThresholds ? [{
                         value: d.previous,
                         group: thresholdGroup,
                         offset: offset
                       }] : []
                     })

    previous.enter()
            .append('path')
              .attr('class', 'value-indicator previous')
              .merge(previous)

    previous.exit()
            .remove()
  })
}

export default function (id, dimensions) {
  var shortEdge = Math.min(dimensions.gaugeHeight, dimensions.gaugeWidth)

  dimensions.cornerRadius = Math.max(3, (shortEdge / 25) + 1)
  dimensions.innerPadding =  (shortEdge / 15) + 1 // The whitespace between the gauge border and colored rects
  dimensions.rectStrokeWidth = dimensions.innerPadding * 0.4 // Stroke-width of threshold rects
  dimensions.spaceBetweenEachBar = dimensions.rectStrokeWidth * 2

  var draw = function (selection) {
    selection.each(function (data) {
      var gauge = d3.select(this)
                    .selectAll('g.gauge').data([data])
        , _thresholds = data.thresholds || [0, 1] // If no thresholds, set to 0,1; so we get a rect
        , flip = _thresholds[0] > _thresholds[1]

       var identical = !!(data.colors && data.colors.length > 1 && _thresholds.reduce(function (p, c) {
                         return (p === c) ? p : NaN
                       }))

       if (identical) {
        // All the thresholds are the same. Generate random threshold blocks
        _thresholds = _thresholds.map(function (t, i) {
          // Generate the smallest possible gap between the thresholds so the current label is displayed
          // at the min or max correctly
          return t + (i * .0000000000000001)
        })
       }

      dimensions.gapSpace = dimensions.spaceBetweenEachBar * (_thresholds.length - 2)

      var range = [0, dimensions.dominantEdge - (dimensions.innerPadding * 2) - dimensions.gapSpace]
        , scaler = scale.scaleLinear()
                        .domain(array.extent(_thresholds))
                        .range(flip ? range.reverse() : range)
                        .clamp(true)
        , enter = gauge.enter()
                       .append('g')
                         .attr('class', 'gauge')
        , _gauge = enter.merge(gauge)

      enter.append('use')
           .attr('class', 'gauge-border-shadow')
           .attr('mask', 'url(#mask-' + id + ')')
           .attr('filter', 'url(#shadow-' + id + ')')
           .attr('xlink:href', '#border-shape-' + id)
      enter.append('use')
           .attr('class', 'gauge-border')
           .attr('xlink:href', '#border-shape-' + id)

      _gauge.attr('transform', 'translate(' + dimensions.margin.left + ', ' + dimensions.margin.top + ')')

      // Draw the actual gauge rects
      var thresholds = _gauge.selectAll('g.thresholds')
                             .data(function (d) {
                               d._thresholds = (d.colors || [d.value && d.value.color || '']).map(function (color, i) {
                                var _threshold = {
                                   color: color,
                                   y0: _thresholds[i], // the start value of the threshold
                                   y1: _thresholds[1 + i], // the end value of this threshold
                                   offset: i * dimensions.spaceBetweenEachBar, // amount of whitespace this threshold has before it
                                   start: scaler(_thresholds[i]) + (dimensions.spaceBetweenEachBar * i), // the starting x or y for the threshold
                                   length: Math.max(0, scaler(_thresholds[1 + i]) - scaler(_thresholds[i])) // width or height, depending on gauge type
                                 }

                                 if (i > 0) {
                                   // center the offset between rects
                                   _threshold.offset -= dimensions.spaceBetweenEachBar / 2
                                 }

                                 return _threshold
                               })
                               return [d]
                             })
        , bisector = flip ? array.bisector(function (t, raw) {
                              return t.y1 < raw ? 1 : -1
                            }).right
                          : array.bisector(function (t, raw) {
                              return t.y1 - raw
                            }).right
        , validThresholds = !!(data.thresholds && data.thresholds.length > 0)

      // How much whitespace will be between all the rects
      if (data.value && typeof data.value.raw !== 'undefined') {
        var thresholdGroup = bisector(data._thresholds, data.value.raw)

        if (data._thresholds[thresholdGroup]) {
          data.value.offset = data._thresholds[thresholdGroup].offset
        } else {
          // At the max
          data.value.offset = data._thresholds[data._thresholds.length - 1].offset + (dimensions.spaceBetweenEachBar / 2)
        }
      }

      thresholds.enter()
                .append('g')
                  .attr('class', 'thresholds')
                  .merge(thresholds)
                  .call(rect, id)
                  .call(actual, validThresholds)
                  .call(previous, dimensions, _thresholds, bisector, validThresholds)
                  .call(currentLabel)
                  .call(labels, dimensions, scaler)

      if (d3.select(this).classed('vertical')) {
        _gauge.call(vertical, id, dimensions, scaler, validThresholds)
      } else {
        _gauge.call(horizontal, id, dimensions, scaler, validThresholds)
      }

    })
  }

  return draw
}
